import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from '@tanstack/react-router'
import { DocumentUploadStep } from './DocumentUploadStep'
import { PropertyQuestionnaireStep } from './PropertyQuestionnaireStep'
import { WizardStepHeader } from './WizardStepHeader'
import { DataReliabilityTab } from './data-reliability/DataReliabilityTab'
import { Button } from './ui/button'
import { clampStep, CONFIG_WIZARD_STEPS } from './onboardingSteps'
import { useAsset, useUpdateAsset } from '../hooks/useQueries'
import { useApi } from '../api/hooks'

interface BuildingConfigWizardProps {
  buildingGroupUid?: string
  resumeStep?: number
}

export function BuildingConfigWizard({ buildingGroupUid, resumeStep }: BuildingConfigWizardProps) {
  const { org } = useParams({ from: '/$org/createBuilding' })
  const navigate = useNavigate()
  const { t } = useTranslation('assets')
  const { t: tCommon } = useTranslation('common')

  // This wizard owns steps 3-5 (documents, properties questionnaire,
  // fiabilisation). Clamp the resume step so a stale value never lands out
  // of bounds.
  const [currentStep, setCurrentStep] = useState<number>(clampStep(resumeStep, CONFIG_WIZARD_STEPS.min, CONFIG_WIZARD_STEPS.max))

  const { request } = useApi()

  // Mutation for updating the building group's frontend_data (incomplete flag / step).
  const updateBuildingGroupMutation = useUpdateAsset(org, buildingGroupUid || '')

  // Fetch building group data if buildingGroupUid is provided
  const { data: buildingGroupAsset, isLoading: isLoadingBuildingGroup } = useAsset(
    org,
    buildingGroupUid || ''
  )

  // Update building group to set incomplete: false when we reach the config phase
  // (documents, step 3) — the structural part of onboarding (group + buildings) is done.
  const incompleteUpdateSent = useRef(false)
  useEffect(() => {
    if (currentStep === 3 && buildingGroupUid && buildingGroupAsset && !incompleteUpdateSent.current) {
      const isCurrentlyIncomplete = buildingGroupAsset.frontend_data?.incomplete === true

      if (isCurrentlyIncomplete) {
        incompleteUpdateSent.current = true
        updateBuildingGroupMutation.mutateAsync({
          frontend_data: {
            ...buildingGroupAsset.frontend_data,
            incomplete: false
          }
        }).catch(error => {
          console.error('Error updating building group incomplete flag:', error)
          incompleteUpdateSent.current = false
        })
      }
    }
  }, [currentStep, buildingGroupUid, buildingGroupAsset, updateBuildingGroupMutation])

  const updateWizardStep = async (assetUid: string, step: number) => {
    try {
      await request('/api/v1/organizations/{orgCode}/assets/{assetUid}', 'PATCH', {
        params: { path: { orgCode: org, assetUid } },
        body: { frontend_data: { incomplete: true, last_wizard_step: step } }
      })
    } catch (err) {
      console.error('Failed to update wizard step:', err)
    }
  }

  const finishWizard = async () => {
    // Mark building group as complete. Fiabilisation (step 5) is non-blocking
    // by design (spec 2026-06-29-buildtory-data-reliability.md §3.1) — this
    // finishes the wizard regardless of whether the building's Data
    // Reliability queue is empty; unresolved items simply persist to the
    // building and are picked up later from its Data tab.
    if (buildingGroupUid) {
      await updateWizardStep(buildingGroupUid, CONFIG_WIZARD_STEPS.max)
      try {
        await updateBuildingGroupMutation.mutateAsync({
          frontend_data: { incomplete: false, last_wizard_step: CONFIG_WIZARD_STEPS.max }
        })
      } catch (err) {
        console.error('Failed to mark building group as complete:', err)
      }
    }
    navigate({
      to: '/$org/assets',
      params: { org },
      search: { q: '', asset_type_code: undefined, asset_type_uid: undefined, flag: undefined, showBuildingGroups: undefined, showBuildings: undefined, archived: undefined }
    })
  }

  const handleDocumentUploadNext = async () => {
    if (buildingGroupUid) await updateWizardStep(buildingGroupUid, 3)
    navigate({
      to: '/$org/createBuilding',
      params: { org },
      search: { building_group_uid: buildingGroupUid, step: 4 }
    })
    setCurrentStep(4)
  }

  const handleQuestionnaireNext = async () => {
    if (buildingGroupUid) await updateWizardStep(buildingGroupUid, 5)
    navigate({
      to: '/$org/createBuilding',
      params: { org },
      search: { building_group_uid: buildingGroupUid, step: 5 }
    })
    setCurrentStep(5)
  }

  const handleQuestionnaireFillLater = async () => {
    if (buildingGroupUid) await updateWizardStep(buildingGroupUid, 4)
    // Keep incomplete flag — navigate to assets. "Fill in later" skips both
    // the properties questionnaire AND fiabilisation (both are revisitable
    // from the building's own tabs afterwards, per spec §3).
    navigate({
      to: '/$org/assets',
      params: { org },
      search: { q: '', asset_type_code: undefined, asset_type_uid: undefined, flag: undefined, showBuildingGroups: undefined, showBuildings: undefined, archived: undefined }
    })
  }

  const handleBack = () => {
    if (currentStep === 5) {
      setCurrentStep(4)
      navigate({
        to: '/$org/createBuilding',
        params: { org },
        search: { building_group_uid: buildingGroupUid, step: 4 }
      })
    } else if (currentStep === 4) {
      setCurrentStep(3)
      navigate({
        to: '/$org/createBuilding',
        params: { org },
        search: { building_group_uid: buildingGroupUid, step: 3 }
      })
    } else {
      // First config step — leave the wizard back to the assets list.
      navigate({
        to: '/$org/assets',
        params: { org },
        search: { q: '', asset_type_code: undefined, asset_type_uid: undefined, flag: undefined, showBuildingGroups: undefined, showBuildings: undefined, archived: undefined }
      })
    }
  }

  // Show loading state while fetching the building group
  if (isLoadingBuildingGroup) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card rounded-lg p-6">
              <WizardStepHeader step={currentStep} title={t('create_building.loading_buildings')} />
              <p className="text-muted-foreground">
                {t('create_building.loading_buildings_msg')}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container mx-auto px-4">
        {currentStep === 3 && buildingGroupUid && (
          <DocumentUploadStep
            org={org}
            assetUid={buildingGroupUid}
            onNext={handleDocumentUploadNext}
            onBack={handleBack}
            onSkip={handleDocumentUploadNext}
            onFinish={finishWizard}
          />
        )}

        {currentStep === 4 && buildingGroupUid && (
          <PropertyQuestionnaireStep
            org={org}
            assetUid={buildingGroupUid}
            onNext={handleQuestionnaireNext}
            onFillLater={handleQuestionnaireFillLater}
            onBack={handleBack}
          />
        )}

        {currentStep === 5 && buildingGroupUid && (
          <div className="max-w-5xl mx-auto">
            <div className="bg-card rounded-lg p-6">
              <WizardStepHeader step={5} title={t('create_building.fiabilisation.title')} />
              <DataReliabilityTab org={org} assetUid={buildingGroupUid} />
              {/* Navigation — finishing here is non-blocking (spec §3.1): the
                  queue may be non-empty, unresolved items simply persist to
                  the building and are picked up later from its Data tab. */}
              <div className="mt-8 pt-6 border-t border-border flex justify-between">
                <Button variant="outline" onClick={handleBack} data-testid="fiabilisation-back-btn">
                  {tCommon('actions.back')}
                </Button>
                <Button onClick={finishWizard} className="bg-primary hover:bg-primary/90" data-testid="fiabilisation-finish-btn">
                  {tCommon('actions.finish')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
